import { memo, useMemo, useRef } from 'react';
import { cn, Gallery } from '@divlab/divanui';

import Link from '@Navigation/Link';
import useRouteMatch from '@Navigation/useRouteMatch';
import IconArrowTriple from './elements/ArrowTriple';
import styles from './InformationTabsNavigation.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { IGalleryRef } from '@divlab/divanui';
import type { InfoNavigationItem, NavigationItemData } from '@Types/NavigationItem';

export interface InformationTabsNavigationProps extends HTMLAttributes<HTMLDivElement> {
  navigation: NavigationItemData[] | InfoNavigationItem[];
}

const icons = {
  arrowTriple: IconArrowTriple,
};

interface NavigationLinkProps extends HTMLAttributes<HTMLDivElement> {
  item: NavigationItemData;
  onClick?: () => void;
  IconBefore?: any;
}

const NavigationLink: FC<NavigationLinkProps> = (props) => {
  const { item, IconBefore, onClick } = props;
  const active = !!useRouteMatch({ path: item.link }) || item.active;

  return (
    <Link
      data-testid={item.testId}
      to={item.link}
      className={cn(styles.navigationLink, { [styles.active]: active })}
      onClick={onClick}
    >
      {!!IconBefore && (
        <IconBefore
          active={item.active}
          className={cn({
            [styles.arrowTriple]: item.before === 'arrowTriple',
          })}
        />
      )}
      {item.title}
    </Link>
  );
};

const InformationTabsNavigation: FC<InformationTabsNavigationProps> = (props) => {
  const { navigation, ...restProps } = props;
  const galleryRef = useRef<IGalleryRef>(null);

  const initialSlideIndex = useMemo(() => {
    return navigation.findIndex((link) => link.active);
  }, [navigation]);

  const handleSlideTo = (index: number) => {
    if (!galleryRef.current) return;

    galleryRef.current.slideTo(index);
  };

  return (
    <nav {...restProps}>
      <Gallery
        className={styles.gallery}
        cnSlide={styles.slide}
        initialSlideIndex={initialSlideIndex || 0}
        ref={galleryRef}
      >
        {navigation.map((item, index) => {
          const IconBefore = icons[item.before];

          const navItem = 'href' in item ? { ...item, link: item.href, title: item.name } : item;

          return (
            <NavigationLink
              key={navItem.title}
              item={navItem}
              onClick={() => handleSlideTo(index)}
              IconBefore={IconBefore}
            />
          );
        })}
      </Gallery>
    </nav>
  );
};

export default memo(InformationTabsNavigation);
